import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { NotAvailableState } from "@/components/ui/system-state";

/**
 * Policy Outcomes tile — design port (ALL-5825, dashboard lane task 4).
 *
 * The prototype tile renders a `SegmentedGauge` pass rate plus Passed /
 * Held / Blocked breakdown rows, all from `getPolicyOutcomes()` — a fixture in
 * `features/oversight/oversight-data`. There is no policy-evaluation endpoint
 * in this app at all — no `/v1/policies` or evaluation route appears in
 * `src/lib/api.ts` — and the designed target `/governance/evaluation` is a
 * `DeferredPage` for the same reason.
 *
 * A pass-rate gauge and "N of M evaluations" caption built from a fixture would
 * be operational numbers with no source — the fabricated-UI case ALL-4747 G6
 * names — so the tile keeps the designed header and renders the designed
 * not-available state. No percentage, count or ratio is estimated here.
 *
 * The header link is kept as designed: it points at the deferred
 * `/governance/evaluation` page, and the not-available body guards the tile from
 * reading as a live pass rate.
 */
export function PolicyOutcomes() {
  return (
    <div className="flex h-full flex-col">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-bold uppercase tracking-[0.06em] text-muted">Policy Outcomes</h2>
          <div className="mt-0.5 text-xs text-muted">Evaluations today</div>
        </div>
        <Link
          to="/governance/evaluation"
          className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-primary hover:underline"
        >
          Evaluations <ArrowUpRight className="size-3.5" aria-hidden />
        </Link>
      </div>
      <NotAvailableState
        className="flex-1 justify-center"
        title="Policy outcomes are not available yet"
        description="Today's policy evaluations, pass rate and Passed / Held / Blocked split are not collected in this environment, so no pass-rate gauge or outcome counts are shown here."
        reason="No gateway route serves policy evaluation results — the /v1/policies routes read policy configuration only."
      />
    </div>
  );
}
